"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "@/hooks/useTranslation";
import { useRTL } from "@/hooks/useRTL";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  X,
  UserCheck,
  AlertTriangle,
  Coffee,
  Cigarette,
  Wine,
  Cake,
  Users,
  Calendar,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function CandidatesSection() {
  const { t, isLoading } = useTranslation("teeth-whitening");
  const { isRTL } = useRTL();
  const [activeTab, setActiveTab] = useState("ideal");
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading) return;

    const ctx = gsap.context(() => {
      // Title animation
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // Content animation
      if (contentRef.current) {
        gsap.fromTo(
          contentRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: contentRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading]);

  const idealCandidates = [
    {
      icon: Coffee,
      text: t("candidates.ideal.coffee_stains"),
      description: t("candidates.ideal.coffee_stains_desc"),
    },
    {
      icon: Cigarette,
      text: t("candidates.ideal.tobacco_stains"),
      description: t("candidates.ideal.tobacco_stains_desc"),
    },
    {
      icon: Wine,
      text: t("candidates.ideal.wine_stains"),
      description: t("candidates.ideal.wine_stains_desc"),
    },
    {
      icon: Calendar,
      text: t("candidates.ideal.aging_teeth"),
      description: t("candidates.ideal.aging_teeth_desc"),
    },
    {
      icon: Cake,
      text: t("candidates.ideal.food_stains"),
      description: t("candidates.ideal.food_stains_desc"),
    },
    {
      icon: Users,
      text: t("candidates.ideal.healthy_teeth"),
      description: t("candidates.ideal.healthy_teeth_desc"),
    },
  ];

  const notIdealCandidates = [
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.intrinsic_stains"),
      description: t("candidates.notIdeal.intrinsic_stains_desc"),
    },
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.pregnant_nursing"),
      description: t("candidates.notIdeal.pregnant_nursing_desc"),
    },
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.sensitivity"),
      description: t("candidates.notIdeal.sensitivity_desc"),
    },
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.gum_disease"),
      description: t("candidates.notIdeal.gum_disease_desc"),
    },
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.dental_work"),
      description: t("candidates.notIdeal.dental_work_desc"),
    },
    {
      icon: AlertTriangle,
      text: t("candidates.notIdeal.young_age"),
      description: t("candidates.notIdeal.young_age_desc"),
    },
  ];

  if (isLoading) {
    return (
      <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
        <div className="container">
          <div className="text-center mb-16 animate-pulse">
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded mb-6 max-w-4xl mx-auto"></div>
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded max-w-3xl mx-auto"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="candidates"
      ref={sectionRef}
      className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900"
    >
      <div className="container">
        <div ref={titleRef} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
            {t("candidates.title.part1")}{" "}
            <span className="text-gold">
              {t("candidates.title.part2")}
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            {t("candidates.subtitle")}
          </p>
        </div>

        <div ref={contentRef} className="max-w-6xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg min-h-[80px]">
              <div className="flex" role="tablist" onKeyDown={(e) => {
                const tabs = ['ideal', 'notIdeal'];
                const currentIndex = tabs.indexOf(activeTab);
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const direction = (e.key === 'ArrowRight') !== isRTL ? 1 : -1;
                  const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
                  setActiveTab(tabs[nextIndex]);
                  document.getElementById(`tab-${tabs[nextIndex]}`)?.focus();
                }
              }}>
                <button
                  role="tab"
                  id="tab-ideal"
                  aria-selected={activeTab === "ideal"}
                  aria-controls="panel-ideal"
                  tabIndex={activeTab === "ideal" ? 0 : -1}
                  onClick={() => setActiveTab("ideal")}
                  className={`px-4 sm:px-6 py-4 sm:py-5 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap text-sm md:text-base sm:min-w-[160px] min-h-14 sm:min-h-16 ${
                    activeTab === "ideal"
                      ? "bg-gold text-navy shadow-md"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                  }`}
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{t("candidates.tabs.ideal")}</span>
                </button>
                <button
                  role="tab"
                  id="tab-notIdeal"
                  aria-selected={activeTab === "notIdeal"}
                  aria-controls="panel-notIdeal"
                  tabIndex={activeTab === "notIdeal" ? 0 : -1}
                  onClick={() => setActiveTab("notIdeal")}
                  className={`px-4 sm:px-6 py-4 sm:py-5 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap text-sm md:text-base sm:min-w-[160px] min-h-14 sm:min-h-16 ${
                    activeTab === "notIdeal"
                      ? "bg-gold text-navy shadow-md"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                  }`}
                >
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span>{t("candidates.tabs.considerations")}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {activeTab === "ideal" && (
              <div role="tabpanel" id="panel-ideal" aria-labelledby="tab-ideal" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {idealCandidates.map((candidate, index) => (
                  <Card
                    key={index}
                    className="border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-300 hover:border-green-500/50 bg-white dark:bg-slate-800"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                          <candidate.icon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                      <h3 className="font-bold text-navy dark:text-white mb-2">
                        {candidate.text}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        {candidate.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {activeTab === "notIdeal" && (
              <div role="tabpanel" id="panel-notIdeal" aria-labelledby="tab-notIdeal" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {notIdealCandidates.map((candidate, index) => (
                  <Card
                    key={index}
                    className="border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-300 hover:border-orange-500/50 bg-white dark:bg-slate-800"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                          <candidate.icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                        </div>
                        <X className="w-6 h-6 text-orange-500" />
                      </div>
                      <h3 className="font-bold text-navy dark:text-white mb-2">
                        {candidate.text}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        {candidate.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="mt-16">
            <Card className="bg-gold/10 border-gold/30">
              <CardContent className="p-8 text-center">
                <div
                  className={`flex items-center justify-center gap-3 mb-6 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  <UserCheck className="w-8 h-8 text-gold" />
                  <h3 className="text-2xl font-bold text-navy dark:text-white">
                    {t("candidates.cta.title")}
                  </h3>
                </div>
                <p
                  className={`text-slate-600 dark:text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto ${
                    isRTL ? "text-right" : "text-center"
                  }`}
                >
                  {t("candidates.cta.description")}
                </p>
                <Button
                  className="bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => {
                    const pricingSection = document.getElementById("pricing");
                    pricingSection?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {t("candidates.cta.button")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
